import * as React from "react";
import { Button, notification } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { request } from "../../../services/cpln";
import { getBillingErrorMessage } from "../../../services/utils";
import { formActions } from "../../../store/formSlice";
import { useAppDispatch } from "../../../store/hooks";
import { CpFormContext } from "../../../components/form/CpFormContext";
import { getDefaultBillingAccountCreateInput, inputToBillingAccount } from "../../account/utils";
import { formSet, formSetValue, useCpFormValue, useCpFormAnyValue } from "../../gvc/testNewForm/utils";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { BillingAccountCreateInput } from "../../account/create/input";
import { store } from "../../../store";
import { getValidatorCreate } from "../../account/validator";
import { AccountCreateInfo } from "../../billing/account/create/info";
import { AccountCreateAddress } from "../../billing/account/create/address";
import { CpFormValidator } from "../../gvc/testNewForm/CpFormValidator";
import { IS_DEPLOYMENT_ENV_TEST } from "../../../envVariables";

interface Props {
  providerId: string;
  awsAccountId: string;
  customerIdentifier: string;
}

const CreateAccountForm: React.FC<Props> = ({ providerId, awsAccountId, customerIdentifier }) => {
  const { id } = useCpFormContext();
  const validator = getValidatorCreate(id);

  const step = useCpFormValue(`meta.formStep`);
  const tou = useCpFormValue(`account.tou`);
  const submitting = useCpFormAnyValue(`meta.submitting`);
  const createdAccountId = useCpFormValue(`meta.createdAccount.id`);
  const createdAccountIsDisabled = useCpFormValue(`meta.createdAccount.disabled`);
  const accountName = useCpFormValue(`account.accountName`);

  async function handleCreateAccount() {
    if (!tou) return;

    try {
      formSet(id, `meta.submitting`, true);

      const form = store.getState().form.instances[id];
      const values: BillingAccountCreateInput = form.values as any;
      const body: any = inputToBillingAccount(values);

      // Add AWS provider fields
      body.provider = "aws";
      body.providerId = providerId;
      body.providerState = {
        awsAccountId: awsAccountId,
        customerIdentifier: customerIdentifier,
      };

      const { data: newAccount } = await request({
        method: "post",
        service: "billing-ng",
        url: `/account`,
        body: { account: body },
      });

      const _accountId = newAccount.id;
      const _isDisabled = newAccount.enabled === false;
      formSetValue(id, `meta.createdAccount`, { disabled: _isDisabled, id: _accountId });

      // Accept TOU
      try {
        await request({
          method: "post",
          service: "billing-ng",
          url: `/account/${newAccount.id}/tou`,
          body: {
            tou: {
              customerName: newAccount.fullName,
              accepted: tou,
            },
          },
        });
      } catch (e) {
        const errorMessage = getBillingErrorMessage(e);
        console.error("Failed to succeed on tou", errorMessage);
        notification.warning({
          message: "Failed to accept terms of service",
          description: errorMessage,
        });
      }

      formSet(id, `meta.submitting`, false);

      // Redirect to account detail page
      window.location.href = `/home/account/${newAccount.id}`;
    } catch (e) {
      notification.warning({
        message: "Failed",
        description: getBillingErrorMessage(e),
      });
      formSet(id, `meta.submitting`, false);
    }
  }

  return (
    <div>
      <CpFormValidator validationFn={validator} />
      {step === "info" ? (
        <>
          <AccountCreateInfo />
          <div className="flex gap-4 mt-4">
            <div className="basis-0 flex-grow" />
            <Button
              className="basis-0 flex-grow"
              type="primary"
              iconPosition="end"
              icon={<ArrowRightOutlined />}
              onClick={async () => {
                formSet(id, `meta.submitted`, true);
                const form = store.getState().form.instances[id];
                const values: BillingAccountCreateInput = form.values as any;
                const hasErrors = await validator(values);
                if (hasErrors) return;
                formSetValue(id, `meta.formStep`, "address");
              }}
            >
              Next (Address)
            </Button>
          </div>
        </>
      ) : null}
      {step === "address" ? (
        <>
          <AccountCreateAddress />
          <div className="flex gap-4 mt-4">
            <Button
              className="basis-0 flex-grow"
              type="default"
              iconPosition="start"
              icon={<ArrowLeftOutlined />}
              disabled={submitting}
              onClick={() => formSetValue(id, `meta.formStep`, "info")}
            >
              Back (Info)
            </Button>
            <Button
              className="basis-0 flex-grow action"
              type="primary"
              disabled={submitting}
              loading={submitting}
              onClick={async () => {
                formSet(id, `meta.submitted`, true);
                const form = store.getState().form.instances[id];
                const values: BillingAccountCreateInput = form.values as any;
                const hasErrors = await validator(values);
                if (hasErrors) return;
                await handleCreateAccount();
              }}
            >
              Create Billing Account
            </Button>
          </div>
        </>
      ) : null}
      {createdAccountId ? (
        <div className="mt-12 flex justify-center">
          <div>Account created, please wait while we are setting up your dashboard.</div>
        </div>
      ) : null}
      {IS_DEPLOYMENT_ENV_TEST && createdAccountId ? (
        <div className="mt-12">
          <div>ONLY VISIBLE ON TEST</div>
          <div>
            Created Account <span className="font-semibold">{accountName}</span>
          </div>
          <div>
            ID <span className="font-semibold">{createdAccountId}</span>
          </div>
          {createdAccountIsDisabled ? <div>Account is disabled</div> : null}
        </div>
      ) : null}
    </div>
  );
};

export const CreateAccount: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const formIdRef = React.useRef(uuidv4());
  const [isInitialized, setIsInitialized] = React.useState(false);

  React.useEffect(() => {
    dispatch(
      formActions.setInitialValues({
        id: formIdRef.current,
        values: getDefaultBillingAccountCreateInput(),
      }),
    );
    setIsInitialized(true);

    return () => {
      dispatch(formActions.clear(formIdRef.current));
    };
  }, []);

  if (!isInitialized) return null;

  return (
    <CpFormContext.Provider value={{ submit: () => Promise.resolve(), id: formIdRef.current }}>
      <CreateAccountForm {...props} />
    </CpFormContext.Provider>
  );
};
